import {
  FilteredPagedQueryResult,
  FilteredPagedModel,
  EntityCommonInfoModel,
  CommonMultiDeleteModel,
  CommonEntityActiveSwitch,
} from "../../entities/common";
import { AccountModel } from "../../entities/account";
import { accountingApiSlice, TagType } from "./accountingApiSlice";
import { getQuerySearchParams } from "../../utils";

export const accountApi = accountingApiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getAccountsCommonInfo: builder.query<EntityCommonInfoModel, undefined>({
      query: () => "/account/commonInfo",
      providesTags: [{ type: TagType.Account, id: "LIST" }],
    }),
    getAccountsFilteredPaged: builder.query<FilteredPagedQueryResult<AccountModel>, FilteredPagedModel>({
      query: ({ page = 1, pageSize = 10, searchTerm = "", sortFieldName = "", descending = false, ...rest }: FilteredPagedModel) => {
        let url = `/account/pageable?page=${page}&pageSize=${pageSize}&searchTerm=${searchTerm}&sortFieldName=${sortFieldName}&descending=${descending}`;
        const restParams = getQuerySearchParams(rest);
        if (restParams) {
          url += `&${restParams}`;
        }
        return url;
      },
      providesTags: (result, error, arg) =>
        result
          ? [{ type: TagType.Account, id: "PARTIAL-LIST" }, ...result.data.map(({ id }) => ({ type: TagType.Account, id }))]
          : [{ type: TagType.Account, id: "PARTIAL-LIST" }],
    }),
    getAccountSelectFilter: builder.query<AccountModel[], string>({
      query: (filterText: string) => `/account/select/${filterText}`,
      providesTags: (data = [], error, arg) => [
        { type: TagType.Account, id: "PARTIAL-LIST" },
        ...data?.map(({ id }: { id: string }) => ({ type: TagType.Account, id })),
      ],
    }),
    getAccount: builder.query<AccountModel, string>({
      query: (id) => `/account/${id}`,
      providesTags: (result, error, arg) => [{ type: TagType.Account, id: arg }],
    }),
    addAccount: builder.mutation({
      query: (account) => ({
        url: "/account",
        method: "POST",
        body: account,
      }),
      invalidatesTags: [
        { type: TagType.Account, id: "LIST" },
        { type: TagType.Account, id: "PARTIAL-LIST" },
      ],
    }),
    switchActiveAccount: builder.mutation({
      query: (model: CommonEntityActiveSwitch) => ({
        url: `/account/switchActive/${model.id}`,
        method: "PUT",
        body: model,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: TagType.Account, id: arg.id },
        { type: TagType.Account, id: "LIST" },
      ],
    }),
    deleteAccount: builder.mutation({
      query: (catId) => ({
        url: `/account/${catId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              { type: TagType.Account, id: arg },
              { type: TagType.Account, id: "LIST" },
            ],
    }),
    deleteMultiAccounts: builder.mutation({
      query: (multiDeleteModel: CommonMultiDeleteModel) => ({
        url: "/account/multiDelete",
        method: "DELETE",
        body: multiDeleteModel,
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              ...arg.ids.map((id: number) => ({
                type: TagType.Account,
                id,
              })),
              { type: TagType.Account, id: "LIST" },
            ],
    }),
  }),
});

export const {
  useGetAccountsCommonInfoQuery,
  useGetAccountsFilteredPagedQuery,
  useGetAccountSelectFilterQuery,
  useGetAccountQuery,
  useAddAccountMutation,
  useSwitchActiveAccountMutation,
  useDeleteAccountMutation,
  useDeleteMultiAccountsMutation,
} = accountApi;
